import {
    getBindingCatalog,
    type RuntimeBindingDescriptor,
} from '../runtime/dataServices/bindingCatalog';
import type { BindingId, RuntimeResultLinkDescriptor } from '../runtime/dataServices/types';
import type { ElementDefinition, ElementInsertionPresetDefinition } from './types';

interface CreateRuntimeBindingInsertionPresetsArgs<
    TValueKind extends RuntimeResultLinkDescriptor['valueKind'],
    TDefinition extends ElementDefinition
> {
    valueKind: TValueKind;
    keyPrefix?: string;
    includeLinksRequiringArg?: boolean;
    filterDescriptor?: (
        descriptor: RuntimeBindingDescriptor<
            Extract<RuntimeResultLinkDescriptor, { valueKind: TValueKind }>
        >
    ) => boolean;
    createDefinitionForBindingId: (
        bindingId: BindingId,
        descriptor: RuntimeBindingDescriptor<
            Extract<RuntimeResultLinkDescriptor, { valueKind: TValueKind }>
        >
    ) => TDefinition;
}

export function createRuntimeBindingInsertionPresetsForKind<
    TValueKind extends RuntimeResultLinkDescriptor['valueKind'],
    TDefinition extends ElementDefinition
>({
    valueKind,
    keyPrefix,
    includeLinksRequiringArg = false,
    filterDescriptor,
    createDefinitionForBindingId,
}: CreateRuntimeBindingInsertionPresetsArgs<
    TValueKind,
    TDefinition
>): readonly ElementInsertionPresetDefinition<TDefinition>[] {
    const catalog = getBindingCatalog();
    const descriptors = catalog.listForValueKind(valueKind);
    const publicDescriptors = descriptors.filter(
        (descriptor) => descriptor.link.exposeInInsertionPresets
    );
    const argCompatibleDescriptors = includeLinksRequiringArg
        ? publicDescriptors
        : publicDescriptors.filter((descriptor) => descriptor.link.argPolicy !== 'required');
    const filteredDescriptors = filterDescriptor
        ? argCompatibleDescriptors.filter(filterDescriptor)
        : argCompatibleDescriptors;

    return filteredDescriptors.map((descriptor) => ({
        key: catalog.toPresetKey(descriptor.bindingId, keyPrefix),
        label: descriptor.link.label,
        description: descriptor.link.description,
        compatibleContextKinds: descriptor.link.supportedContextKinds,
        supportedSwarmTypes: descriptor.link.supportedSwarmTypes,
        createDefinition: () => createDefinitionForBindingId(descriptor.bindingId, descriptor),
    }));
}
